import React, { useState } from 'react';
import { Star } from 'lucide-react';

export default function ResponsiveTestComponent() {
  const [containerWidth, setContainerWidth] = useState(220);
  const [isResizing, setIsResizing] = useState(false);

  const handleMouseDown = (e) => {
    setIsResizing(true);
    e.preventDefault();
  };

  // Move handleMouseMove and handleMouseUp inside useEffect or wrap with useCallback
  const handleMouseMove = React.useCallback((e) => {
    if (isResizing) {
      const newWidth = Math.max(220, Math.min(960, e.clientX - 20));
      setContainerWidth(newWidth);
    }
  }, [isResizing]);

  const handleMouseUp = React.useCallback(() => {
    setIsResizing(false);
  }, []);

  React.useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizing, handleMouseMove, handleMouseUp]);

  const getScreenSize = () => {
    if (containerWidth < 640) return 'sm';
    if (containerWidth < 768) return 'md';
    return 'lg';
  };

  const screenSize = getScreenSize();

  return (
    <div className="lg:p-6 p-4 rounded-lg bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700">
      <div 
        className="relative bg-color-white border border-color-gray-200 rounded-md dark:bg-color-gray-800 dark:border-color-gray-700"
        style={{ width: containerWidth }}
      >
        {/* Main Content */}
        <div className={`
          ${screenSize === 'sm' ? 'block' : 'flex'}
          ${screenSize === 'md' ? 'flex-col lg:flex-row' : ''}
        `}>
                  <div className={`
                    ${screenSize === 'sm' ? 'p-4' : 'p-6'}
                    ${screenSize === 'lg' ? 'flex-1 pr-6' : 'w-full'}
                  `}>
                    <div className="mb-2">
                      <span className="text-sm text-color-gray-600 dark:text-color-gray-400">Entire villa</span>
                    </div>
                    
                    <h1 className={`
                      font-bold text-color-gray-900 mb-3 dark:text-color-white
                      ${screenSize === 'sm' ? 'text-xl' : 'text-2xl lg:text-3xl'}
                    `}>
                      Modern Villa by the Sea
                    </h1>

                    <div className="flex items-center gap-2 mb-4">
                      <Star className="w-4 h-4 fill-color-yellow-500 text-color-yellow-500 dark:text-color-yellow-400" />
                      <span className="font-medium text-color-yellow-500 dark:text-color-yellow-400">4.85</span>
                      <span className="text-color-gray-600 dark:text-color-gray-400">(342 reviews)</span>
                    </div>

                    <p className={`
                      text-color-gray-700 mb-6 dark:text-color-gray-400
                      ${screenSize === 'sm' ? 'text-sm' : 'text-base'}
                    `}>
                      Enjoy a luxurious stay in this modern villa with breathtaking sea views, 
                      perfect for families or groups seeking relaxation and comfort.
                    </p>

                    {screenSize === 'sm' && (
                      <button className="w-full bg-color-pink-500 hover:bg-color-pink-600 text-color-white font-medium py-3 px-6 rounded-lg transition-color">
                        Reserve now
                      </button>
                    )}

                    {screenSize !== 'sm' && (
                      <button className="bg-color-pink-500 hover:bg-color-pink-600 text-color-white font-medium py-3 px-6 rounded-lg transition-color">
                        Reserve now
                      </button>
                    )}
                  </div>

                  {/* Right Images */}
          <div className={`
            ${screenSize === 'sm' ? 'mt-4' : ''}
            ${screenSize === 'lg' ? 'flex-1 p-4' : 'w-full mt-6 lg:mt-0'}
          `}>
            {screenSize === 'sm' ? (
              // Single image for mobile
              <div className="px-4 pb-4">
                <div className="aspect-video bg-gradient-[linear-gradient(180deg,color-blue-200,color-green-200)] rounded-lg flex items-center justify-center">
                  <span className="text-color-gray-600 text-sm">Main Image</span>
                </div>
              </div>
            ) : (
              // Grid layout for larger screens
              <div className={`
                grid gap-2
                ${screenSize === 'md' ? 'grid-columns-2 px-6' : 'grid-columns-2'}
              `}>
                <div className="col-span-1 row-span-2 bg-gradient-[linear-gradient(180deg,color-blue-200,color-green-200)] rounded-lg flex items-center justify-center">
                  <span className="text-color-gray-600">Beach House</span>
                </div>
                <div className="aspect-3/2 bg-gradient-[linear-gradient(180deg,color-yellow-200,color-orange-200)] rounded-lg flex items-center justify-center">
                  <span className="text-color-gray-600 text-sm">Deck</span>
                </div>
                <div className="aspect-3/2 bg-gradient-[linear-gradient(180deg,color-pink-200,color-purple-200)] rounded-lg flex items-center justify-center">
                  <span className="text-color-gray-600 text-sm">Interior</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Resize Handle */}
        <div
          className="absolute -right-4 top-0 bottom-0 w-4 flex items-center justify-center transition-color"
        >
          <span onMouseDown={handleMouseDown} className="w-2 h-10 bg-color-gray-400 hover:bg-color-gray-500 rounded-full cursor-ew-resize" />
        </div>
      </div>
    </div>
  );
}